import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchInsightCardBySlug } from "@/utils/supabase/insights-helpers";
import NotFound from "./NotFound";
import { useLinkedPolls } from "@/hooks/insights/useLinkedPolls";
import { useInsightRelatedContent } from "@/hooks/useInsightRelatedContent";
import { useVotePermissions } from "@/hooks/polls/useVotePermissions";
import { useAuth } from "@/contexts/auth";
import { Poll } from "@/types/poll";
import InsightLoading from "@/components/insights/InsightLoading";
import InsightContent from "@/components/insights/InsightContent";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import InsightPageHeader from "@/components/insights/InsightPageHeader";
import { Helmet } from "react-helmet";
import { InsightCard } from "@/types/insight";
import { useInsightCardRealtime } from "@/hooks/useInsightCardRealtime";

const InsightPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { canVote } = useVotePermissions();
  const navigate = useNavigate();

  const {
    data: initialCard,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["insight", slug],
    queryFn: () => (slug ? fetchInsightCardBySlug(slug) : null),
    enabled: !!slug,
  });

  // State to track the card data, initialized from the query but can be updated by realtime events
  const [card, setCard] = useState<InsightCard | null>(null);

  // Update local card state when query data changes
  useEffect(() => {
    if (initialCard) {
      setCard(initialCard);
    }
  }, [initialCard]);

  // Handle real-time updates to the card data
  const handleCardUpdate = useCallback((updatedCard: InsightCard) => {
    console.log("InsightPage: Received updated card data:", updatedCard);
    setCard(updatedCard);
  }, []);

  // Set up real-time subscription for this card
  useInsightCardRealtime(card?.id, handleCardUpdate);

  const {
    linkedPolls,
    isLoading: isLoadingPolls,
    handleRefreshPolls,
    isConnected,
  } = useLinkedPolls(card?.id || "", card?.linked_poll || null);

  const [polls, setPolls] = useState<Poll[]>([]);
  const { relatedContent, hasRelatedContent } = useInsightRelatedContent(card);
  const [isOpen, setIsOpen] = React.useState(true);

  // Generate canonical URL for this insight
  const canonicalUrl = card?.slug ? `/insights/${card.slug}` : "";
  const shareableUrl = `${window.location.origin}${canonicalUrl}`;

  useEffect(() => {
    if (card) {
      document.title = `${card.title} | NationCard™ Insight`;
    } else {
      document.title = "Insight | NationCard™";
    }
  }, [card]);

  useEffect(() => {
    console.log("LinkedPolls updated:", linkedPolls);
    setPolls(linkedPolls);
  }, [linkedPolls]);

  if (isLoading) {
    return <InsightLoading />;
  }

  // Fix race condition: check query error state instead of local card state
  if (error || (initialCard === null && !isLoading)) {
    return <NotFound />;
  }

  // Don't render content until we have card data
  if (!card) {
    return <InsightLoading />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>{card.title} | NationCard™ Insight</title>
        <meta
          name="description"
          content={card.description || "View this insight on NationCard™"}
        />
        <meta
          property="og:title"
          content={`${card.title} | NationCard™ Insight`}
        />
        <meta
          property="og:description"
          content={card.description || "View this insight on NationCard™"}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareableUrl} />
        {card.slug && <link rel="canonical" href={shareableUrl} />}
      </Helmet>

      <Header />
      <main className="flex-1 pt-[97px]">
        <div className="container max-w-5xl mx-auto py-8 px-4">
          <InsightPageHeader card={card} />

          <div className="flex flex-col gap-8">
            <InsightContent
              card={card}
              user={user}
              canVote={canVote}
              polls={polls}
              setPolls={setPolls}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              isLoadingPolls={isLoadingPolls}
              handleRefreshPolls={handleRefreshPolls}
              isConnected={isConnected}
              relatedContent={relatedContent}
              hasRelatedContent={hasRelatedContent}
            />
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default InsightPage;
